import { useEffect, useId, useRef, useState } from "react";
import { Bell, Loader2, RefreshCw } from "lucide-react";
import {
  getUserPushTestPreview,
  sendUserPushTest,
  type UserPushTestPreview,
} from "../services/UserPushTestService";

interface UserPushTestCardProps {
  userId: string;
  storeIds: string[];
  banned?: boolean;
}

const money = (value: number) => `$${value.toFixed(2)}`;

export default function UserPushTestCard({ userId, storeIds, banned }: UserPushTestCardProps) {
  const inputId = useId();
  const stores = [...new Set(storeIds.map((id) => id.trim()).filter((id) => id && id !== "online"))];
  const [expanded, setExpanded] = useState(false);
  const [storeId, setStoreId] = useState(stores[0] || "");
  const [refresh, setRefresh] = useState(0);
  const [preview, setPreview] = useState<UserPushTestPreview | null>(null);
  const [upc, setUpc] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");

  useEffect(() => {
    if (!expanded || !storeId || banned) return;
    let cancelled = false;
    setLoading(true);
    setPreview(null);
    setUpc("");
    setError("");
    setResult("");
    getUserPushTestPreview(userId, storeId)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setUpc(data.products[0]?.upc || "");
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load test products");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [expanded, userId, storeId, refresh, banned]);

  const product = preview?.products.find((item) => item.upc === upc && item.storeId === storeId);
  const send = async () => {
    if (!product || !preview?.platforms.length || sendingRef.current || loading || banned) return;
    sendingRef.current = true;
    setSending(true);
    setError("");
    setResult("");
    try {
      const response = await sendUserPushTest(userId, product);
      setResult(response.message);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send the test push");
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const selectClass = "w-full rounded-lg bg-gray-50 dark:bg-dark-700 px-3 py-2 text-sm text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-dark-600 disabled:opacity-50";
  return (
    <section aria-label="Test push notification" className="rounded-2xl bg-white dark:bg-dark-800 p-4 shadow-sm ring-1 ring-gray-200 dark:ring-dark-700">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-[13px] font-bold text-gray-900 dark:text-white"><Bell className="h-4 w-4 text-blue-500" /> Test push notification</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-dark-300">Send a Walmart product to this user's current registered devices.</p>
        </div>
        {!expanded && <button type="button" disabled={!stores.length || banned} onClick={() => setExpanded(true)} className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">Choose test product</button>}
      </div>
      {banned ? <p className="mt-3 text-xs text-gray-500 dark:text-dark-300">Test pushes are unavailable for banned users.</p> : !stores.length ? <p className="mt-3 text-xs text-gray-500 dark:text-dark-300">Add a Walmart store to this user's store list to test a product notification.</p> : expanded && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1">
              <label htmlFor={`${inputId}-store`} className="mb-1 block text-xs font-medium text-gray-600 dark:text-dark-200">Walmart store</label>
              <select id={`${inputId}-store`} value={storeId} disabled={sending} onChange={(e) => { setStoreId(e.target.value); setResult(""); }} className={selectClass}>
                {stores.map((id) => <option key={id} value={id}>Walmart #{id}</option>)}
              </select>
            </div>
            <button type="button" aria-label="Refresh products and push status" title="Refresh products and push status" onClick={() => setRefresh((value) => value + 1)} disabled={loading || sending} className="rounded-lg p-2.5 text-gray-500 ring-1 ring-gray-200 dark:text-dark-200 dark:ring-dark-600 disabled:opacity-50"><RefreshCw className="h-4 w-4" /></button>
          </div>
          {loading && <p role="status" className="flex items-center gap-2 text-xs text-gray-500 dark:text-dark-300"><Loader2 className="h-4 w-4 animate-spin" /> Loading Walmart products and push status…</p>}
          {preview && !loading && <>
            <p className="text-xs text-gray-500 dark:text-dark-300">{preview.platforms.length ? `Registered devices: ${preview.platforms.join(", ")}. Tokens are reloaded when you send.` : "No push tokens registered. Have the user open the app and enable notifications, then refresh."}</p>
            {preview.products.length ? <>
              <div>
                <label htmlFor={`${inputId}-product`} className="mb-1 block text-xs font-medium text-gray-600 dark:text-dark-200">Recent Walmart product</label>
                <select id={`${inputId}-product`} value={upc} disabled={sending} onChange={(e) => { setUpc(e.target.value); setResult(""); }} className={selectClass}>
                  {preview.products.map((item) => <option key={item.upc} value={item.upc}>{item.productName} — {money(item.price)}</option>)}
                </select>
              </div>
              {product && <div className="flex items-center gap-3 rounded-xl bg-gray-50 dark:bg-dark-700/60 p-3">
                {product.imageUrl && <img src={product.imageUrl} alt="" className="h-16 w-16 shrink-0 rounded-lg bg-white object-contain" />}
                <div className="min-w-0 text-sm text-gray-900 dark:text-white">
                  <p className="font-semibold">[Test] {product.discountPercent > 0 ? `🔥 ${product.discountPercent}% off at Walmart` : "🔥 Deal alert at Walmart"}</p>
                  <p className="mt-1 break-words">{product.productName}</p>
                  <p className="text-xs text-gray-500 dark:text-dark-300">Now {money(product.price)}{product.msrp > product.price && ` (was ${money(product.msrp)})`} · Walmart #{product.storeId}</p>
                </div>
              </div>}
              <p className="text-xs text-gray-500 dark:text-dark-300">Sends to {userId} using the product detail notification. This manual test bypasses plan and alert filters.</p>
              <button type="button" onClick={send} disabled={!product || !preview.platforms.length || sending} className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}{sending ? "Sending test push…" : "Send test push"}
              </button>
            </> : <p className="text-xs text-gray-500 dark:text-dark-300">No products with a usable price were found among this store's 50 latest updates from the last 3 days. Choose another saved store or refresh later.</p>}
          </>}
          {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          {result && <p role="status" className="text-sm text-emerald-700 dark:text-emerald-400">{result}</p>}
        </div>
      )}
    </section>
  );
}
